import { ExerciseThumbnail } from "../../design-v2";

// Tier-3 builder presentation seam (Phase 7B).
//
// One exercise-search result row. Presentation only. The query, the filters,
// the 250ms debounce, the request timing, `rankExercises`, the User Exercise
// Pool semantics, the excluded-match fallback and `activeSearchTarget` all stay
// in the Workout Builder container (blueprint D23 / section 4.1).
//
// Click semantics are unchanged: the whole row is ONE button that hands the
// search result object back untouched. This row never normalises a pool item --
// `toBuilderExerciseSelection` in the container remains the single boundary
// turning `attributes.bodyParts` / `attributes.muscleFocus` into builder
// taxonomy, so muscle distribution keeps working for newly selected exercises.

// Mockup proportion: a 56px (w-14) square beside a 16px name inside a 8px-padded
// row, which keeps the row target well above 44px.
export const EXERCISE_RESULT_THUMBNAIL_SIZE = 56;

// Metadata is read ONLY from fields the exercise-pool search result already
// carries. Nothing is fetched, derived or invented for presentation.
export function getExerciseResultMeta(exercise) {
  const attributes = exercise?.attributes || {};
  const primaryBodyPart = Array.isArray(attributes.bodyParts)
    ? attributes.bodyParts[0]
    : null;

  return [attributes.equipmentCategory, primaryBodyPart]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
}

function AddIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8.5v7M8.5 12h7" />
    </svg>
  );
}

function SwapIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none">
      <path d="M7 7h11l-3-3M17 17H6l3 3" />
    </svg>
  );
}

// `tags` and `actionIcon` are optional presentation extras used by the
// Workout Log substitute picker (compact conflict tags, a swap glyph); the
// builder never passes them and renders exactly as before.
export default function ExerciseResultRow({
  exercise,
  disabled = false,
  thumbnailSrc = null,
  thumbnailLoading = "lazy",
  thumbnailFetchPriority,
  onSelect,
  tags = null,
  actionIcon = "add",
}) {
  // The name is the row's only text content -- no secondary metadata line --
  // so long titles keep their vertical room to wrap across up to two lines
  // instead of competing with a second line for space.
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(exercise)}
      data-exercise-id={exercise.exerciseId || undefined}
      className="lz-builder-result-row"
    >
      <ExerciseThumbnail
        exerciseId={exercise.exerciseId}
        src={thumbnailSrc}
        size={EXERCISE_RESULT_THUMBNAIL_SIZE}
        alt=""
        loading={thumbnailLoading}
        fetchPriority={thumbnailFetchPriority}
        className="lz-builder-result-row__thumbnail"
      />

      <span className="lz-builder-result-row__identity">
        <span className="lz-builder-result-row__name">{exercise.name}</span>
        {tags?.length ? (
          <span className="lz-builder-result-row__tags">
            {tags.map((tag) => (
              <span key={tag} className="lz-builder-result-row__tag">
                <span aria-hidden="true">⚠</span> {tag}
              </span>
            ))}
          </span>
        ) : null}
      </span>

      <span className="lz-builder-result-row__action" aria-hidden="true">
        {actionIcon === "swap" ? <SwapIcon /> : <AddIcon />}
      </span>
    </button>
  );
}
